import React, { useState } from 'react';

import {
  IonItem,
  IonLabel,
  IonSearchbar,
  IonSpinner,
  IonToolbar,
} from '@ionic/react';

import Button from 'components/Button';
import DataList, { DataListConfig } from 'components/DataList';
import Modal, {
  ModalApplyButton,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalTitle,
} from 'components/Modal';
import Typography from 'components/Typography';

import { useAutoFocus } from '../../hooks/useAutoFocus';

export interface PickerConfig extends DataListConfig {
  title: string;
  toolbarTitle?: string;
  toolbarSubtitle?: string;
  label?: string;
  filter?: (opt: any, query: string) => boolean;
  onSubmit: (data?: string | null | undefined | number, role?: string) => void;
  onClose: (data?: string | null | undefined | number, role?: string) => void;
  apply?: boolean;
  add?: boolean;
  addText?: string;
  onAdd?: (opt: any) => void;
  addModal?: any;
  error?: any;
  components?: { DataList?: any };
  DataListProps?: object;
  isLoading?: boolean;
  withSearchbar?: boolean;
  children?: React.ReactNode;
}

const Picker = ({
  title,
  toolbarTitle,
  toolbarSubtitle,
  label,
  data = [],
  filter,
  error,
  onSubmit,
  onClose,
  onItemClick,
  isSelected,
  apply,
  getLabel,
  getBadge,
  secondaryActions,
  primaryActions,
  add,
  addText = 'Add',
  onAdd,
  components,
  DataListProps,
  isLoading,
  withSearchbar = true,
  children,
}: PickerConfig) => {
  const [query, setQuery] = useState('');
  const ref = useAutoFocus<HTMLIonSearchbarElement>({
    enabled: data.length >= 15,
  });
  const filteredData = filter
    ? data.filter((opt: any) => {
        return filter(opt, query);
      })
    : data;
  const handleChangeQuery = (e: any) => setQuery(e.detail.value);

  const ComponentDataList = components?.DataList || DataList;

  return (
    <Modal onSubmit={onSubmit} onClose={onClose}>
      <IonToolbar>
        {withSearchbar ? (
          <IonSearchbar
            value={query}
            onIonChange={handleChangeQuery}
            ref={ref}
          />
        ) : null}
        {toolbarTitle ? (
          <Typography variant={'caption'}>{toolbarTitle}</Typography>
        ) : null}
        {toolbarSubtitle ? (
          <Typography variant={'subtitle1'}>{toolbarSubtitle}</Typography>
        ) : null}
      </IonToolbar>
      <ModalContent>
        {label ? (
          <IonItem lines={'none'}>
            <IonLabel>
              <Typography variant={'caption'}>{label}</Typography>
            </IonLabel>
          </IonItem>
        ) : null}
        {isLoading ? (
          <div className={'flex-center'}>
            <IonSpinner
              slot={'end'}
              name={'crescent'}
              color={'medium-shade'}
              className={'spinner'}
            />
            <Typography color={'medium-shade'} className={'ion-padding'}>
              Loading....
            </Typography>
          </div>
        ) : error ? (
          <Typography color={'danger'} className={'ion-padding'}>
            {error}
          </Typography>
        ) : (
          <ComponentDataList
            data={filteredData}
            isSelected={isSelected}
            onItemClick={onItemClick}
            getLabel={getLabel}
            getBadge={getBadge}
            secondaryActions={secondaryActions}
            primaryActions={primaryActions}
            {...DataListProps}
          />
        )}
        {children}
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton />
        <ModalTitle>{title}</ModalTitle>
        {add ? (
          <Button
            slot={'end'}
            className={'ion-padding-end'}
            color={'primary'}
            fill={'clear'}
            onClick={onAdd}
          >
            {addText}
          </Button>
        ) : null}
        {apply ? <ModalApplyButton /> : null}
      </ModalFooter>
    </Modal>
  );
};

export default Picker;
